import { AgGridAngular } from 'ag-grid-angular';
import {
    AllCommunityModule,
    ClientSideRowModelModule,
    ColDef,
    GridApi,
    GridOptions,
    GridReadyEvent,
    IRowNode,
    ModuleRegistry,
} from 'ag-grid-community';
import {
    ClipboardModule,
    ContextMenuModule,
    MasterDetailModule,
} from 'ag-grid-enterprise';

import { AG_GRID_LOCALE_EN } from '@ag-grid-community/locale';
import {
    ChangeDetectionStrategy,
    Component,
    inject,
    Input,
    OnInit,
} from '@angular/core';
import { PushPipe } from '@ngrx/component';
import { AG_GRID_LOCALES } from '@wdx/shared/components/ag-grid';
import { WdxSpinnerModule } from '@wdx/shared/components/wdx-spinner';
import {
    FriendlyProblemDetails,
    InvestmentDataColumnConfiguration,
    InvestmentDataSectionConfiguration,
    StandingOrder,
    WdxAgGridThemeDirective,
} from '@wdx/shared/utils';
import { forkJoin, map, Observable } from 'rxjs';
import { PortfolioConfigName } from '../constants/portfolio-config-names.const';
import {
    PortfolioLoadingStates,
    PortfolioNotLoadedComponent,
} from '../portfolio-not-loaded/portfolio-not-loaded.component';
import { PortfolioAgGridColumnMapperService } from '../services/portfolio-ag-grid-column-mapper.service';
import { PortfolioConfigService } from '../services/portfolio-config.service';
import { PortfolioDetailCellRendererComponent } from '../shared/portfolio-detail-renderer/portfolio-detail-renderer.component';
import {
    STANDING_ORDER_DEFAULT_PRIMARY_COLUMNS,
    STANDING_ORDER_DETAIL_DEFAULT_FIELDS,
} from './standing-order-default-configs.const';

ModuleRegistry.registerModules([
    AllCommunityModule,
    ClientSideRowModelModule,
    MasterDetailModule,
    ClipboardModule,
    ContextMenuModule,
]);

@Component({
    selector: 'wdx-standing-orders',
    imports: [
        AgGridAngular,
        WdxAgGridThemeDirective,
        WdxSpinnerModule,
        PortfolioNotLoadedComponent,
        PushPipe,
    ],
    providers: [PortfolioAgGridColumnMapperService, PortfolioConfigService],
    templateUrl: './standing-orders.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'h-100' },
})
export class StandingOrdersComponent implements OnInit {
    private portfolioConfigService = inject(PortfolioConfigService);
    private agGridColumnMapperService = inject(
        PortfolioAgGridColumnMapperService,
    );

    @Input() dataTypeLabel = 'Standing Order';
    @Input() dataConnectionError?: FriendlyProblemDetails = undefined;
    @Input() localeCode?: string | null | undefined;
    @Input() set standingOrderData(value: StandingOrder[] | undefined | null) {
        if (!value) {
            this.status = 'loading';
            return;
        }

        this.status = 'loaded';
        this.rowData = [...value];
        if (!this.rowData.length) {
            this.status = 'noData';
        }
    }

    public status: PortfolioLoadingStates = 'loading';
    public rowData?: StandingOrder[];

    public detailConfig$?: () => Observable<{
        sections?: InvestmentDataSectionConfiguration[] | null;
        fields?: InvestmentDataColumnConfiguration[];
    }> = () =>
        forkJoin({
            fields: this.portfolioConfigService
                .getExpandedColumns$(
                    PortfolioConfigName.PortfolioStandingOrders,
                )
                .pipe(
                    map((fields) =>
                        fields && fields.length
                            ? fields
                            : STANDING_ORDER_DETAIL_DEFAULT_FIELDS,
                    ),
                ),
            sections: this.portfolioConfigService
                .getWidgetConfig$(PortfolioConfigName.PortfolioStandingOrders)
                .pipe(map((config) => config?.sections)),
        });

    public gridOptions: GridOptions = {
        domLayout: 'autoHeight',
        pagination: true,
        paginationPageSize: 20,
        defaultColDef: {
            flex: 1,
            sortable: true,
            contextMenuItems: ['copy'],
        },
        detailCellRenderer: PortfolioDetailCellRendererComponent,
        detailCellRendererParams: {
            getConfig$: this.detailConfig$,
            updateDetailRowHeight: (rowNode: IRowNode, height: number) =>
                this.updateDetailRowHeight(rowNode, height),
        },
        masterDetail: true,
        detailRowHeight: 80,
        onGridReady: (params: GridReadyEvent) => {
            this.gridApi = params.api;
        },
    };
    public colDefs$?: Observable<ColDef[]>;

    private gridApi?: GridApi;

    ngOnInit(): void {
        const lang = this.localeCode?.split('-')[0] || 'en';
        this.gridOptions.localeText =
            AG_GRID_LOCALES[lang] || AG_GRID_LOCALE_EN;

        this.colDefs$ = this.portfolioConfigService
            .getPrimaryColumns$(PortfolioConfigName.PortfolioStandingOrders)
            .pipe(
                map((columns) =>
                    (columns.length
                        ? columns
                        : STANDING_ORDER_DEFAULT_PRIMARY_COLUMNS
                    )?.map((col) =>
                        this.agGridColumnMapperService.createColDef(col),
                    ),
                ),
                map((columns) => [
                    {
                        headerName: '',
                        flex: undefined,
                        width: 40,
                        maxWidth: 40,
                        cellRenderer: 'agGroupCellRenderer',
                        cellRendererParams: {
                            suppressCount: true,
                        },
                    },
                    ...columns,
                ]),
            );
    }

    private updateDetailRowHeight(rowNode: IRowNode, height: number): void {
        if (!rowNode || !height || !this.gridApi) {
            return;
        }
        rowNode.setRowHeight(height);
        this.gridApi?.onRowHeightChanged();
    }
}
